import { RadialGauge } from "@/components/Gauge";
import { TrustFlags } from "@/components/TrustFlags";
import { useTranslation } from "react-i18next";
import { Check, X, ExternalLink, Award } from "lucide-react";
import { motion } from "framer-motion";

export function RecommendationCard({ recommendation, trustChecks }) {
  const { t } = useTranslation();
  if (!recommendation) return null;
  const r = recommendation;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="card-crisp overflow-hidden"
      data-testid="recommendation-card"
    >
      <div className="border-b border-border bg-secondary/40 p-6 sm:p-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-lg">
            <div className="mb-2 flex items-center gap-2">
              <Award className="h-4 w-4 text-ikb" />
              <span className="text-xs font-semibold uppercase tracking-overline text-ikb">
                {t("recommendation.recommends")}
              </span>
            </div>
            <h2
              className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl"
              data-testid="recommendation-name"
            >
              {r.option_name}
            </h2>
            {r.price_estimate && (
              <p className="mt-1 font-mono text-sm text-muted-foreground">{r.price_estimate}</p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-8">
            <RadialGauge
              value={r.decision_score}
              label="Decision Score"
              sublabel="/ 100"
              tone="score"
              testId="decision-score-gauge"
            />
            <RadialGauge
              value={r.confidence}
              label="Confidence"
              tone="confidence"
              testId="confidence-gauge"
            />
          </div>
        </div>
      </div>

      <div className="space-y-6 p-6 sm:p-8">
        <div>
          <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-overline text-muted-foreground">
            {t("recommendation.why")}
          </h3>
          <p className="text-[15px] leading-relaxed text-foreground" data-testid="recommendation-why">
            {r.why}
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-overline text-success">
              {t("recommendation.pros")}
            </h3>
            <ul className="space-y-2" data-testid="recommendation-pros">
              {(r.pros || []).map((p) => (
                <li key={p} className="flex gap-2 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-overline text-destructive">
              {t("recommendation.cons")}
            </h3>
            <ul className="space-y-2" data-testid="recommendation-cons">
              {(r.cons || []).map((p) => (
                <li key={p} className="flex gap-2 text-sm">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {r.sources?.length > 0 && (
          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-overline text-muted-foreground">
              {t("recommendation.sources")}
            </h3>
            <div className="flex flex-wrap gap-2" data-testid="recommendation-sources">
              {r.sources.map((s, i) => (
                <a
                  key={s.url || s.title || i}
                  href={s.url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium transition-colors hover:border-ikb"
                >
                  <span className="max-w-[220px] truncate">{s.title}</span>
                  <ExternalLink className="h-3 w-3 text-muted-foreground group-hover:text-ikb" />
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-border pt-5">
          <TrustFlags checks={trustChecks} />
        </div>
      </div>
    </motion.div>
  );
}
